import { createContext, useCallback, useContext, useMemo } from "react";
import type { PropsWithChildren } from "react";
import type { AvailableContextsType } from "./hooks/useFilterKitConfiguration";
import { useFilterKitConfiguration } from "./hooks/useFilterKitConfiguration";
import type { FilterConfiguration } from "./types";
import type { BaseFilter } from "./hooks/contexts/types";

interface FilterProviderContextValue {
  clearFilter: (id: string) => void;
  clearFilterByKey: (key: string) => void;
  clearFilters: () => void;
  configurations: FilterConfiguration[];
  filterParamForKey: (key: string) => BaseFilter | undefined;
  filterParams: BaseFilter[];
  filterParamsForKey: (key: string) => BaseFilter[];
  isLoading: boolean;
  setFilter: (id: string, newFilterParam: BaseFilter) => void;
}

const FilterProviderContext = createContext<
  FilterProviderContextValue | undefined
>(undefined);

FilterProviderContext.displayName = "FilterProviderContext";

interface FilterProviderProps {
  clearFilter: (id: string) => void;
  clearFilterByKey: (key: string) => void;
  clearFilters: () => void;
  context: AvailableContextsType;
  filterParamForKey: (key: string) => BaseFilter | undefined;
  filterParams: BaseFilter[];
  filterParamsForKey: (key: string) => BaseFilter[];
  setFilter: (id: string, newFilterParam: BaseFilter) => void;
}

export const FilterProvider = ({
  context,
  children,
  filterParams,
  clearFilter,
  clearFilters,
  clearFilterByKey,
  filterParamForKey,
  filterParamsForKey,
  setFilter,
}: PropsWithChildren<FilterProviderProps>) => {
  const { data, isLoading } = useFilterKitConfiguration({ context });

  const configurations = useMemo(
    () => data?.filter_configurations ?? [],
    [data],
  );

  const setFilterWithIncompatibilities = useCallback(
    (id: string, newFilterParam: BaseFilter) => {
      const filterConfiguration = configurations.find(
        filterConfiguration => filterConfiguration.key === id,
      );

      const incompatibleFilterKeys =
        filterConfiguration?.incompatible_with_filters ?? [];

      incompatibleFilterKeys.forEach(incompatibleFilterKey => {
        clearFilterByKey(incompatibleFilterKey);
      });

      setFilter(id, newFilterParam);
    },
    [clearFilterByKey, configurations, setFilter],
  );

  const value: FilterProviderContextValue = useMemo(
    () => ({
      configurations,
      isLoading,
      filterParams,
      clearFilter,
      clearFilterByKey,
      clearFilters,
      filterParamForKey,
      filterParamsForKey,
      setFilter: setFilterWithIncompatibilities,
    }),
    [
      configurations,
      isLoading,
      filterParams,
      clearFilter,
      clearFilterByKey,
      filterParamForKey,
      filterParamsForKey,
      setFilterWithIncompatibilities,
      clearFilters,
    ],
  );

  return (
    <FilterProviderContext.Provider value={value}>
      {children}
    </FilterProviderContext.Provider>
  );
};

export const useFilterProvider = () => {
  const context = useContext(FilterProviderContext);
  if (!context) {
    throw new Error("useFilterProvider must be used within FilterProvider");
  }
  return context;
};
